'use client';

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Range } from 'react-input-range';

// import InputRange, { Range } from 'react-input-range';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import qs from 'qs';

import openArrowIcon from '@/assets/icons/left-slider-arrow.svg';
import Accordion from '@/components/V2/Accordion';
import Button from '@/components/V2/Button';
import Checkbox from '@/components/V2/Checkbox';
import Radio, { RadioGroup } from '@/components/V2/Radio';
import { categoriesEligibleForFilters } from '@/config/constants';
import useEzShip from '@/helpers/hooks/useEzShip';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { generateEzShipURL } from '@/helpers/utils/urlHelpers';
import { useAppContext } from '@/layouts/AppProvider';
import { useAppSelector } from '@/store';
import { Category } from '@/types/Category';
import { ProductStrapi } from '@/types/Product';

import ProductListingHeader from './ProductListingHeader';

import 'react-input-range/lib/css/index.css';
import './PriceRangeStyle.css';

import { useParseCategoryForAlgolia } from '@/helpers/hooks/useParseCategoryForAlgolia';

const Animate = dynamic(() => import('react-animate-mount').then(module => module.Animate));

interface IProductListingFiltrationProps {
  onFilterChange?: (petNames: Array<string>) => void;

  categories: Category[];
  categoryName?: string;
  className?: string;
}

const ProductListingFiltration = (
  {
    className,

    categories,
  }: IProductListingFiltrationProps // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const pathname = usePathname();
  const { category } = useParams<{ category: string }>();
  const [showFiltersList, setShowFiltersList] = useState(false);
  const [isFilterApplyBtnDisabled, setIsFilterApplyBtnDisabled] = useState(false);
  const [selectedPetIdsList, setSelectedPetIdsList] = useState<any[]>([]);
  const [selectedPetItemCodesList, setSelectedPetItemCodesList] = useState<any[]>([]);

  const [priceRange, setPriceRange] = useState<Range>();
  const [selectedRating, setSelectedRating] = useState<number>();

  const { webalias, generateURL } = useWebAlias();
  const { ezShipId } = useEzShip();
  const router = useRouter();
  const searchParams = useSearchParams();
  const selectedPetIds = useMemo(() => searchParams.get('pets')?.split(',') || [], [searchParams]);
  const selectedItemCodes = useMemo(() => searchParams.get('itemCodes')?.split(',') || [], [searchParams]);
  const searchQuery = searchParams.get('query') || '';
  const autoOrderID = searchParams.get('autoOrderID') || '';
  const { recommendedForItems } = useAppContext();

  const currentCategoryDetailsWithProducts = useAppSelector(
    store =>
      store.categoryDetailsApi.queries['getCategoryDetails(undefined)']?.data as {
        Products: ProductStrapi[];
        Category: Category;
      }
  );

  const { totalNumberOfProductsInACategory, currentCategoryName, currentCategoryProductsCount } = useAppSelector(
    store => store.slices.CategoryProductStatsSlice
  );

  const categoryDetails = useAppSelector(store => store.slices.categoryDetails.categoryDetails);

  const {
    shouldFetchParentId,
    searchQuery: searchQueryForHeader,
    categoryName: catName,
  } = useParseCategoryForAlgolia(categoryDetails);

  const categoryName = (currentCategoryDetailsWithProducts?.Category?.Name || currentCategoryName) ?? '';

  const isEligibleForFilters = categoriesEligibleForFilters.includes(category);
  const showFilters = isEligibleForFilters && (recommendedForItems || []).length > 0;

  const isSearchPage = pathname.includes('/search');

  const generateCategoryURL = (value: string) => {
    const isEligibleForFilters = categoriesEligibleForFilters.includes(value);
    let url = '';
    let pets = selectedPetIdsList;
    let itemCodes = selectedPetItemCodesList;

    if (!isEligibleForFilters) {
      url = generateURL(generateEzShipURL(`/products/${encodeURIComponent(value)}`, ezShipId), webalias);
    } else {
      const params = qs.stringify(
        {
          ...(pets.length > 0 ? { pets: pets } : {}),
          ...(autoOrderID || ezShipId.length ? { autoOrderID: autoOrderID || ezShipId } : {}),
          ...(itemCodes.length > 0 ? { itemCodes: !pets.length ? [] : itemCodes } : {}),
        },
        { arrayFormat: 'comma' }
      );
      const decodedSearchParams = params ? decodeURIComponent(params) : '';
      url = generateURL(
        `/products/${encodeURIComponent(value)}?${decodeURIComponent(decodedSearchParams?.toString())}`,
        webalias
      );
    }

    return url;
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const generatePetFilterURL = () => {
    setIsFilterApplyBtnDisabled(true);
    // let pets = JSON.parse(JSON.stringify(selectedPetIds)) as string[];
    // let itemCodes = JSON.parse(JSON.stringify(selectedItemCodes)) as string[];
    let pets = selectedPetIdsList;
    let itemCodes = selectedPetItemCodesList;

    // if (pets.includes(petProfileId)) {
    //   pets = pets.filter(pet => pet !== petProfileId);
    //   itemCodes = itemCodes.filter(itemCode => petItems.includes(itemCode));
    // } else {
    //   pets = [...pets, petProfileId];
    //   itemCodes = Array.from(new Set([...itemCodes, ...petItems]).values());
    // }

    const params = qs.stringify(
      {
        ...(isSearchPage || searchQuery ? { query: searchQuery } : {}),
        ...(pets.length > 0 ? { pets: pets } : {}),
        ...(autoOrderID || ezShipId.length ? { autoOrderID: autoOrderID || ezShipId } : {}),
        ...(itemCodes.length > 0 ? { itemCodes: !pets.length ? [] : itemCodes } : {}),
        ...(Object.keys(priceRange || {}).length > 0 ? { price: `${priceRange?.min} TO ${priceRange?.max}` } : {}),
        ...(selectedRating ? { rating: selectedRating === 5 ? `5` : `${selectedRating} TO 5` } : {}),
      },
      { arrayFormat: 'comma' }
    );
    const decodedSearchParams = params ? decodeURIComponent(params) : '';

    let url = isSearchPage
      ? generateURL(generateEzShipURL(`/products/search`, ezShipId), webalias)
      : generateURL(generateEzShipURL(`/products/${encodeURIComponent(category)}`, ezShipId), webalias);

    url = ezShipId
      ? decodedSearchParams
        ? `${url}${url.includes('?') ? '&' : '?'}${decodedSearchParams}`
        : url
      : decodedSearchParams
      ? `${url}?${decodedSearchParams}`
      : url;
    router.push(url);
  };

  useEffect(() => {
    setSelectedPetIdsList(selectedPetIds);
    setSelectedPetItemCodesList(selectedItemCodes);
    setIsFilterApplyBtnDisabled(false);
  }, [searchParams, selectedItemCodes, selectedPetIds]);

  const handlePetProfileIdChange = (petProfileId: string, petItems: Array<string>) => {
    let pets: any[] = selectedPetIdsList;
    let itemCodes: any[] = selectedPetItemCodesList;
    if (pets.includes(petProfileId)) {
      pets = pets.filter(pet => pet !== petProfileId);
      itemCodes = itemCodes.filter(itemCode => petItems.includes(itemCode));
    } else {
      pets = [...pets, petProfileId];
      itemCodes = Array.from(new Set([...itemCodes, ...petItems]).values());
    }
    setSelectedPetIdsList(pets);
    setSelectedPetItemCodesList(itemCodes);
  };

  const searchUrl = generateURL(generateEzShipURL(`/products/search`, ezShipId), webalias);

  const resetURL = isSearchPage
    ? searchUrl.includes('?')
      ? `${searchUrl}&query=${searchQuery}`
      : `${searchUrl}?query=${searchQuery}`
    : generateURL(generateEzShipURL(`/products/${encodeURIComponent(category)}`, ezShipId), webalias);

  return (
    <aside className={cn('v2-w-full sm:v2-w-[256px] sm:v2-mb-6', className)}>
      {!isSearchPage && (
        <ProductListingHeader
          searchQuery={searchQueryForHeader}
          categoryName={categoryName}
          shouldFetchParentId={shouldFetchParentId}
          wrapperClassName="!v2-text-[29px] v2-text-black v2-flex v2-flex-wrap v2-items-center sm:v2-hidden v2-mb-6"
        />
      )}

      <Button
        variant="outlined"
        btnSize="md"
        scheme="secondary"
        className={cn('sm:v2-hidden v2-w-full sm:v2-mb-6 v2-flex v2-justify-center v2-items-center v2-gap-2', {
          // 'v2-hidden': !showFilters,
        })}
        onClick={() => setShowFiltersList(!showFiltersList)}
        type="button"
      >
        <span>Filters</span>{' '}
        <Image
          src={openArrowIcon}
          alt="open filters"
          className={cn('v2-transition-all v2-mt-1', showFiltersList ? 'v2-rotate-90' : '-v2-rotate-90')}
        />
      </Button>

      <div className="v2-hidden sm:v2-block">
        <div className="v2-flex v2-flex-col v2-gap-3  v2-divide-y v2-divide-gray-300">
          <div>
            <h3 className="v2-font-sweet-sans-pro-semi-bold v2-capitalize v2-font-semibold v2-text-lg v2-mt-2 v2-text-neutral-800 v2-flex v2-justify-between v2-items-center">
              Categories
            </h3>
            {categories && categories.length > 0 ? (
              <RadioGroup
                key={`categories-sidebar-${isSearchPage}`}
                initialSelectedValue={isSearchPage ? '' : categoryName?.toLowerCase()}
                name="category"
                className="v2-mt-3 v2-flex v2-flex-col v2-gap-3"
              >
                {categories?.map(item => (
                  <Radio
                    as="Link"
                    defaultValue={item?.Name?.toLowerCase() || ''}
                    href={generateCategoryURL(item?.id?.toString() || '')}
                    key={item.Name || item.id}
                    className="v2-rounded-xs after:v2-h-[20px] after:v2-w-[20px] !v2-w-[20px] !v2-top-[7px] !v2-h-[20px] after:v2-rounded-xs v2-border-[#c8d2d1] after:v2-border-primary"
                  >
                    <span
                      className="v2-font-sweet-sans-pro-light v2-font-light v2-text-[17px] v2-text-neutral-800"
                      key={item.id}
                      title={item.Name}
                    >
                      {item.Name} <span className="v2-text-[13px] v2-text-neutral-500">({item.ProductCount})</span>
                    </span>
                  </Radio>
                ))}
              </RadioGroup>
            ) : (
              <p className="v2-font-sweet-sans-pro v2-text-xs v2-text-gray-600 v2-mt-3">No Categories found.</p>
            )}
          </div>
          <Fragment key={pathname}>
            <div className={cn('sm:v2-block v2-hidden', { '!v2-hidden': !showFilters })}>
              <h3 className="v2-mt-3 v2-font-sweet-sans-pro-semi-bold v2-font-semibold v2-text-base v2-text-neutral-800">
                Filters
              </h3>
            </div>
            {showFilters && (
              <Accordion title="Pet" open>
                <div className="v2-mt-3 v2-flex v2-flex-col v2-gap-3">
                  {recommendedForItems?.map(pet => (
                    <Checkbox
                      key={pet.petProfileId}
                      value={selectedPetIdsList.includes(pet.petProfileId + '')}
                      containerClassName="items-center"
                      childrenClassName="flex items-center gap-2 v2-overflow-hidden"
                      id={pet.petProfileId}
                      name={`Pet ${pet?.petProfileId}`}
                      disabled={isFilterApplyBtnDisabled}
                      onChange={() => handlePetProfileIdChange(`${pet?.petProfileId}`, pet?.itemCodes)}
                      // href={generatePetFilterURL(`${pet?.petProfileId}`, pet?.itemCodes)}
                    >
                      <span
                        title={pet.petProfileName}
                        className="v2-font-sweet-sans-pro-light v2-capitalize v2-font-light v2-text-[16px] v2-text-neutral-800 v2-overflow-hidden v2-text-ellipsis v2-whitespace-nowrap"
                      >
                        {pet.petProfileName}
                      </span>
                    </Checkbox>
                  ))}
                </div>
              </Accordion>
            )}
            {/* <Accordion title="Price" open>
              <div className="v2-mt-3 v2-flex v2-flex-col v2-gap-3">
                <InputRange
                  draggableTrack
                  maxValue={250}
                  minValue={0}
                  onChange={value => setPriceRange(value as Range)}
                  onChangeComplete={value => console.log(value)}
                  value={priceRange || { min: 0, max: 250 }}
                />
              </div>
            </Accordion> */}
            {/* <Accordion title="Rating" open>
              <div className="v2-mt-3 v2-flex v2-flex-col v2-gap-3">
                {Array.from({ length: 5 }, (_, i) => i + 1)
                  .reverse()
                  .map(rating => (
                    <Checkbox
                      key={rating}
                      value={selectedRating === rating}
                      containerClassName="items-center"
                      childrenClassName="flex items-center gap-2 v2-overflow-hidden"
                      id={`rating-component-${rating}`}
                      name={`Rating ${rating}`}
                      onChange={() => setSelectedRating(rating)}
                    >
                      <span className="v2-font-sweet-sans-pro-light v2-gap-1 v2-capitalize v2-font-light v2-text-[16px] v2-text-neutral-800 v2-overflow-hidden v2-text-ellipsis v2-whitespace-nowrap v2-flex">
                  

                        {Array.from({ length: rating }, (_, i) => i + 1).map((_, index) => (
                          <Rating key={`rating-${rating}-key-${_}`} isFilled={true} />
                        ))}
                    
                        {Array.from({ length: 5 - rating }, (_, i) => i + 1).map((_, index) => (
                          <Rating key={`rating-${rating}-key-${_}`} isFilled={false} />
                        ))}

                        {rating < 5 ? ' & Up' : ''}
                      </span>
                    </Checkbox>
                  ))}
              </div>
            </Accordion> */}
          </Fragment>
        </div>
        {showFilters && (
          <div className="v2-flex v2-mt-6 v2-gap-2">
            <Button
              type="button"
              scheme="primary"
              variant="filled"
              btnSize="xs"
              className="v2-w-full v2-text-center"
              disabled={isFilterApplyBtnDisabled}
              onClick={() => generatePetFilterURL()}
            >
              Apply
            </Button>
            <Button
              type="button"
              scheme="primary"
              variant="outlined"
              btnSize="xs"
              className="v2-w-full v2-text-center"
              href={resetURL}
              onClick={e => {
                e.preventDefault();
                setPriceRange(undefined);
                setSelectedRating(undefined);
                router.push(resetURL);
              }}
            >
              Reset
            </Button>
          </div>
        )}
      </div>
      <div className="sm:v2-hidden v2-block v2-px-3">
        <Animate show={showFiltersList} type="slide">
          <div className="v2-flex v2-flex-col v2-gap-3 v2-divide-y v2-divide-gray-300">
            <Accordion title="Categories" open>
              {categories && categories.length > 0 ? (
                <RadioGroup
                  key={`categories-desktop-sidebar-${isSearchPage}`}
                  initialSelectedValue={isSearchPage ? '' : categoryName?.toLowerCase()}
                  name="category"
                  className="v2-mt-3 v2-flex v2-flex-col v2-gap-3"
                >
                  {categories?.map(item => (
                    <Radio
                      as="Link"
                      defaultValue={item?.Name?.toLowerCase() || ''}
                      href={generateCategoryURL(item?.id?.toString() || '')}
                      key={item.Name || item.id}
                      className="v2-rounded-xs after:v2-h-[20px] after:v2-w-[20px] !v2-w-[20px] !v2-top-[7px] !v2-h-[20px] after:v2-rounded-xs v2-border-[#c8d2d1] after:v2-border-primary"
                    >
                      <span
                        className="v2-font-sweet-sans-pro-light v2-font-light v2-text-[16px] v2-text-neutral-800"
                        key={item.id}
                        title={item.Name}
                      >
                        {item.Name} <span className="v2-text-sm v2-text-neutral-500">({item.ProductCount})</span>
                      </span>
                    </Radio>
                  ))}
                </RadioGroup>
              ) : (
                <p className="v2-font-sweet-sans-pro v2-text-xs v2-text-gray-600 v2-mt-3">No Categories found.</p>
              )}
            </Accordion>
            <Fragment key={pathname}>
              {showFilters && (
                <Accordion title="Pet" open>
                  <div className="v2-mt-3 v2-flex v2-flex-col v2-gap-3">
                    {recommendedForItems?.map(pet => (
                      <Checkbox
                        // as="Link"
                        key={pet.petProfileId}
                        value={selectedPetIdsList.includes(pet.petProfileId + '')}
                        containerClassName="items-center"
                        childrenClassName="flex items-center gap-2 v2-overflow-hidden"
                        id={pet.petProfileId}
                        name={`Pet ${pet?.petProfileId}`}
                        disabled={isFilterApplyBtnDisabled}
                        onChange={() => handlePetProfileIdChange(`${pet?.petProfileId}`, pet?.itemCodes)}
                        // href={generatePetFilterURL(`${pet?.petProfileId}`, pet?.itemCodes)}
                      >
                        <span
                          title={pet.petProfileName}
                          className="v2-font-sweet-sans-pro-light v2-capitalize v2-font-light v2-text-[16px] v2-text-neutral-800 v2-overflow-hidden v2-text-ellipsis v2-whitespace-nowrap"
                        >
                          {pet.petProfileName}
                        </span>
                      </Checkbox>
                    ))}
                  </div>
                </Accordion>
              )}
              {/* <Accordion title="Price" open>
                <div className="v2-mt-3 v2-flex v2-flex-col v2-gap-3">
                  <InputRange
                    draggableTrack
                    maxValue={250}
                    minValue={0}
                    onChange={value => setPriceRange(value as Range)}
                    onChangeComplete={value => console.log(value)}
                    value={priceRange || { min: 0, max: 250 }}
                  />
                </div>
              </Accordion> */}
              {/* <Accordion title="Rating" open>
              <div className="v2-mt-3 v2-flex v2-flex-col v2-gap-3">
                {Array.from({ length: 5 }, (_, i) => i + 1)
                  .reverse()
                  .map(rating => (
                    <Checkbox
                      key={rating}
                      value={selectedRating === rating}
                      containerClassName="items-center"
                      childrenClassName="flex items-center gap-2 v2-overflow-hidden"
                      id={`rating-component-${rating}`}
                      name={`Rating ${rating}`}
                      onChange={() => setSelectedRating(rating)}
                    >
                      <span className="v2-font-sweet-sans-pro-light v2-flex v2-gap-1 v2-capitalize v2-font-light v2-text-[16px] v2-text-neutral-800 v2-overflow-hidden v2-text-ellipsis v2-whitespace-nowrap">
                        

                        {Array.from({ length: rating }, (_, i) => i + 1).map((_, index) => (
                          <Rating key={`rating-${rating}-key-${_}`} isFilled={true} />
                        ))}
                     
                        {Array.from({ length: 5 - rating }, (_, i) => i + 1).map((_, index) => (
                          <Rating key={`rating-${rating}-key-${_}`} isFilled={false} />
                        ))}

                        {rating < 5 ? ' & Up' : ''}
                      </span>
                    </Checkbox>
                  ))}
              </div>
            </Accordion> */}
            </Fragment>
          </div>
          {showFilters && (
            <div className="v2-flex v2-mt-6 v2-flex-col v2-gap-2">
              <Button
                type="button"
                scheme="primary"
                variant="filled"
                btnSize="xs"
                className="v2-w-full v2-text-center"
                disabled={isFilterApplyBtnDisabled}
                onClick={() => generatePetFilterURL()}
              >
                Apply
              </Button>
              <Button
                type="button"
                scheme="primary"
                variant="outlined"
                btnSize="xs"
                className="v2-w-full v2-text-center"
                href={resetURL}
                onClick={e => {
                  e.preventDefault();
                  setPriceRange(undefined);
                  setSelectedRating(undefined);
                  router.push(resetURL);
                }}
              >
                Reset
              </Button>
            </div>
          )}
        </Animate>
      </div>
      {/* <div className="sm:v2-block v2-hidden">
        <hr className="v2-my-4 v2-border-gray-300" />
        <h3 className="v2-font-sweet-sans-pro-semi-bold v2-font-semibold v2-text-base v2-text-neutral-800">Filters</h3>
        <hr className="v2-my-4 v2-border-gray-300" />
      </div> */}
    </aside>
  );
};

export default ProductListingFiltration;
